import { motion, useInView } from 'framer-motion'
import { useRef, useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { fadeInUp, staggerContainer } from '@/lib/motion'
import { cn } from '@/lib/utils'

// Wire shapes verified against:
//   app/controllers/api/v1/rooms_controller.rb#join, #publish, #subscribe
//   docs/openapi.yaml /api/v1/rooms/{room_id}/{join,publish,subscribe}
const codeExamples = [
  {
    id: 'join',
    label: 'Join room',
    file: 'join.ts',
    code: `// Join a room — one POST and you have an Cloudflare SFU session.
// The X-Participant-Id header must match \`participant_id\` in the body.
const res = await fetch(
  'https://api.konexzero.com/api/v1/rooms/standup-2026-05/join',
  {
    method: 'POST',
    headers: {
      'X-App-Id':         APP_ID,
      'X-Api-Key':        API_KEY,
      'X-Participant-Id': 'user-123',
      'Content-Type':     'application/json',
    },
    body: JSON.stringify({
      participant_id: 'user-123',
      display_name:   'Alice',
      // optional — pass on a re-join after a transient drop to recover the
      // same SFU session instead of allocating a new one.
      // resume_session_id: priorSessionId,
    }),
  },
)

const { data } = await res.json()
const { session_id, ice_servers, ws_token, resumed } = data`,
  },
  {
    id: 'publish',
    label: 'Publish',
    file: 'publish.ts',
    code: `// Publish local tracks. Each track has a name + kind ("audio" / "video")
// and a mid from the local SDP offer. POST returns the SDP answer.
const stream = await navigator.mediaDevices.getUserMedia({
  video: true,
  audio: true,
})

const res = await fetch(
  'https://api.konexzero.com/api/v1/rooms/standup-2026-05/publish',
  {
    method: 'POST',
    headers: {
      ...baseHeaders,
      'X-Participant-Id': 'user-123',
    },
    body: JSON.stringify({
      tracks: [
        { track_name: 'video', kind: 'video', mid: '0' },
        { track_name: 'audio', kind: 'audio', mid: '1' },
      ],
      session_description: localOffer, // { type: 'offer', sdp }
    }),
  },
)

const { data: { session_description: answer } } = await res.json()
await pc.setRemoteDescription(answer)`,
  },
  {
    id: 'subscribe',
    label: 'Subscribe',
    file: 'subscribe.ts',
    code: `// Subscribe by participant_id, not by session. Returns an SDP offer
// that you complete by POSTing your answer to /subscribe/answer.
const res = await fetch(
  'https://api.konexzero.com/api/v1/rooms/standup-2026-05/subscribe',
  {
    method: 'POST',
    headers: {
      ...baseHeaders,
      'X-Participant-Id': 'user-456',
    },
    body: JSON.stringify({
      subscriptions: [
        { participant_id: 'user-123', track_names: ['video', 'audio'] },
      ],
    }),
  },
)

// 503 with code:"tracks_not_ready" can occur right after the publisher
// just published — retry after Retry-After-Ms ms before failing.
const { data: { session_description: offer } } = await res.json()`,
  },
]

function CodeBlock({ code, file }: { code: string; file: string }) {
  const [copied, setCopied] = useState(false)
  const lines = code.split('\n')

  function copy() {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div
      className="surface-card relative overflow-hidden font-mono"
      style={{
        background:
          'linear-gradient(180deg, hsl(230 30% 8%) 0%, hsl(230 30% 6%) 100%)',
      }}
    >
      {/* Window chrome */}
      <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-3">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
        </div>
        <span className="ml-2 truncate text-[11px] uppercase tracking-[0.18em] text-zinc-500">
          {file}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto h-7 w-7 text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
          onClick={copy}
          aria-label={copied ? 'Copied' : 'Copy code'}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Code body — manual line numbers */}
      <div className="overflow-x-auto px-5 py-5 text-[13px] leading-[1.7]">
        <table className="w-full border-collapse">
          <tbody>
            {lines.map((ln, i) => (
              <tr key={i}>
                <td
                  aria-hidden
                  className="select-none pr-5 text-right align-top text-zinc-600 tabular-nums"
                  style={{ width: '2ch' }}
                >
                  {i + 1}
                </td>
                <td className="whitespace-pre text-zinc-200">{ln || ' '}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function CodeExampleSection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [tab, setTab] = useState(codeExamples[0].id)
  const active = codeExamples.find((e) => e.id === tab)!

  return (
    <section aria-label="Code examples" className="relative py-28 lg:py-40">
      <div className="mx-auto max-w-5xl px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-12 max-w-2xl"
        >
          <motion.div variants={fadeInUp} className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            04 / surface area
          </motion.div>
          <motion.h2
            variants={fadeInUp}
            className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl"
          >
            Ship in Minutes, Not Months
          </motion.h2>
          <motion.p variants={fadeInUp} className="mt-5 text-lg text-muted-foreground">
            Simple REST calls. No proprietary SDKs. Just HTTP and WebRTC.
          </motion.p>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Custom pill tabs */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {codeExamples.map((ex) => (
              <button
                key={ex.id}
                type="button"
                onClick={() => setTab(ex.id)}
                className={cn(
                  'rounded-md border px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] transition-all',
                  tab === ex.id
                    ? 'border-transparent bg-foreground text-background'
                    : 'border-foreground/10 text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                )}
              >
                {ex.label}
              </button>
            ))}
            <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground/60">
              POST · application/json
            </span>
          </div>

          <CodeBlock code={active.code} file={active.file} />
        </motion.div>
      </div>
    </section>
  )
}
